import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import clientLogger from '../utils/clientLogger';
import { prysmDB } from '../utils/database';
import apiService from '../services/apiService';
import { getVisitorUUID } from '../utils/uuidManager';
import ErrorModal from './ErrorModal';

export const SquarePaymentCard = ({ onPaymentSuccess, onPaymentError }) => {
    const navigate = useNavigate();

    const [promoCode, setPromoCode] = useState('');
    const [promoApplied, setPromoApplied] = useState(false);
    const [promoError, setPromoError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const [showErrorModal, setShowErrorModal] = useState(false);

    const debounceTimeoutRef = useRef(null);

    // ================================
    // LOAD SAVED STATE ON MOUNT
    // ================================

    useEffect(() => {
        const loadFromStorage = async () => {
            try {
                const registrationData = await prysmDB.get('registration_data', 'current_registration');
                if (registrationData?.promoCode && registrationData?.promoApplied) {
                    setPromoCode(registrationData.promoCode);
                    setPromoApplied(true);
                }
            } catch (err) {
                console.error('[SquarePaymentCard] Error loading from storage:', err);
            }
        };
        loadFromStorage();

        return () => {
            if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
        };
    }, []);

    // ================================
    // PROMO CODE
    // ================================

    const validatePromoCode = useCallback(async (code) => {
        if (!code.trim()) {
            setPromoError('');
            setPromoApplied(false);
            return;
        }

        setPromoApplied(false);
        setPromoError('');

        try {
            const registrationData = await prysmDB.get('registration_data', 'current_registration');
            if (!registrationData) {
                setPromoError('Please complete registration first');
                return;
            }

            const { email: userEmail, gender: userGender } = registrationData;

            if (!userEmail) { setPromoError('Email address not found in registration data'); return; }
            if (!userGender) { setPromoError('Gender information not found in registration data'); return; }

            const response = await apiService.checkPromoCode(code.toUpperCase(), userEmail, userGender);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to validate promotional code');
            }

            const result = await response.json();

            if (result.status === true) {
                setPromoApplied(true);
                setPromoError('');

                registrationData.promoCode = code.toUpperCase();
                registrationData.promoApplied = true;
                registrationData.promoTimestamp = new Date().toISOString();
                await prysmDB.set('registration_data', 'current_registration', registrationData);
                window.dispatchEvent(new CustomEvent('registrationDataUpdated', { detail: registrationData }));
            } else {
                setPromoError(result.message || 'Invalid promotional code');
            }
        } catch (err) {
            setPromoError(err.message || 'Error validating promotional code');
            clientLogger.logValidationError('promoCode', err, { promoCode: code });
        }
    }, []);

    const handlePromoCodeChange = useCallback((e) => {
        const code = e.target.value;
        setPromoCode(code);
        setPromoError('');

        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

        if (code.length >= 3) {
            debounceTimeoutRef.current = setTimeout(() => validatePromoCode(code), 500);
        } else if (code.length === 0) {
            setPromoApplied(false);
        }
    }, [validatePromoCode]);

    const removePromoCode = useCallback(() => {
        setPromoCode('');
        setPromoApplied(false);
        setPromoError('');

        const clearFromStorage = async () => {
            try {
                const registrationData = await prysmDB.get('registration_data', 'current_registration');
                if (registrationData) {
                    delete registrationData.promoCode;
                    delete registrationData.promoApplied;
                    delete registrationData.promoTimestamp;
                    await prysmDB.set('registration_data', 'current_registration', registrationData);
                    window.dispatchEvent(new CustomEvent('registrationDataUpdated', { detail: registrationData }));
                }
            } catch (err) {
                console.error('[SquarePaymentCard] Error removing promo code from storage:', err);
            }
        };

        clearFromStorage();
    }, []);

    // ================================
    // SUBMIT
    // ================================

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (isProcessing || submitted) return;

        setIsProcessing(true);

        try {
            const registrationData = await prysmDB.get('registration_data', 'current_registration');
            const visitorUUID = await getVisitorUUID();

            if (!visitorUUID) throw new Error('Visitor UUID not found. Please refresh the page and try again.');

            const freeRegistrationData = {
                'data-type': 'free-registration',
                visitorUUID,
                email: registrationData?.email || '',
                amount: 0,
                promoCode: promoCode || null,
                planId: 'free',
                planName: 'Free Registration',
                timestamp: new Date().toISOString()
            };

            const response = await apiService.processFreeRegistration(freeRegistrationData);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to process registration');
            }

            const result = await response.json();

            await prysmDB.set('payment_result', 'current_payment', {
                freeRegistration: true,
                amount: 0,
                promoCode: promoCode || null,
                success: true,
                timestamp: new Date().toISOString()
            });

            setSubmitted(true);

            if (onPaymentSuccess) onPaymentSuccess(result);

            navigate('/upload-selfie');
        } catch (err) {
            console.error('[SquarePaymentCard] Registration error:', err);
            setError({ message: err.message || 'Registration failed. Please try again.' });
            setShowErrorModal(true);
            if (onPaymentError) onPaymentError(err);
        } finally {
            setIsProcessing(false);
        }
    }, [isProcessing, submitted, promoCode, onPaymentSuccess, onPaymentError, navigate]);

    // ================================
    // RENDER
    // ================================

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4 relative overflow-hidden">
            {/* Decorative background */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
                <div className="absolute top-40 left-40 w-80 h-80 bg-gray-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            <div className="w-full max-w-md relative z-10">
                <div className="bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-gray-700">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-500 mb-4">
                            Free Registration
                        </h1>
                        <div className="h-1 w-20 bg-gradient-to-r from-pink-500 to-purple-600 mx-auto rounded-full"></div>
                        <p className="mt-4 text-gray-400">
                            Complete your registration below
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Promotional Code */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-300">
                                Promotional Code <span className="text-gray-500">(optional)</span>
                            </label>
                            <div className="flex gap-3">
                                <div className="flex-1">
                                    <input
                                        type="text"
                                        value={promoCode}
                                        onChange={handlePromoCodeChange}
                                        placeholder="Enter promotional code"
                                        disabled={isProcessing}
                                        className={`w-full px-3 py-2 border rounded-xl bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500 transition-all duration-200 ${
                                            promoError
                                                ? 'border-red-400 bg-red-900/20'
                                                : promoApplied
                                                    ? 'border-green-400 bg-green-900/20'
                                                    : 'border-gray-600'
                                        }`}
                                    />
                                    {promoError && (
                                        <p className="text-red-400 text-sm mt-1">{promoError}</p>
                                    )}
                                    {promoApplied && (
                                        <div className="flex items-center text-green-400 text-sm mt-1">
                                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                            Promotional code applied!
                                        </div>
                                    )}
                                </div>
                                {promoApplied ? (
                                    <button
                                        type="button"
                                        onClick={removePromoCode}
                                        className="px-4 py-2 text-sm text-gray-300 hover:text-white border border-gray-600 rounded-xl hover:bg-gray-700 transition-colors"
                                    >
                                        Remove
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => validatePromoCode(promoCode)}
                                        disabled={!promoCode.trim() || isProcessing}
                                        className="px-4 py-2 text-sm text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-xl transition-colors"
                                    >
                                        Apply
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={isProcessing || submitted}
                            className="w-full rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 text-white px-4 py-3 font-semibold transition-all duration-200 hover:from-pink-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                            {isProcessing ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Please Wait...
                                </>
                            ) : submitted ? 'Registration Submitted' : 'Complete Registration'}
                        </button>
                    </form>
                </div>
            </div>

            <ErrorModal
                isOpen={showErrorModal}
                onClose={() => setShowErrorModal(false)}
                error={error}
                title="Registration Error"
                showRetry={true}
                onRetry={() => { setError(null); setShowErrorModal(false); }}
            />
        </div>
    );
};

export default SquarePaymentCard;
